<template>
    <div class="investment-platform">
        <v-container fluid>
            <!-- Header -->
            <v-row>
                <v-col cols="12">
                    <v-card class="platform-header mb-6" elevation="4">
                        <v-card-title class="text-h4 primary--text text-center py-8">
                            <div class="w-100">
                                <v-icon x-large left color="primary">mdi-trending-up</v-icon>
                                <div class="text-h3 font-weight-bold">Investičná platforma</div>
                                <!-- <div class="text-h6 grey--text mt-2">Staňte sa súčasťou nášho úspechu</div> -->
                            </div>
                        </v-card-title>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Tabuľka 1: Modelácia výšky investície (VZOR pre investorov) -->
            <v-row class="mb-6">
                <v-col cols="12">
                    <investment-calculator ref="calculator" :defaultAnnualRevenue="defaultAnnualRevenue"
                        :totalInvestments="totalInvestments" :maxInvestmentAmount="maxInvestmentAmount"
                        :myInactiveInvestmentsTotal="myInactiveInvestmentsTotal"
                        :activeInvestmentsTotal="activeInvestmentsTotal" @invest="handleInvestmentSubmission"
                        @annual-revenue-change="handleAnnualRevenueChange"
                        :defaultPayoutPercentage="modeledPayoutPercentage"
                        @payout-percentage-change="handlePayoutPercentageChange"
                        @show-contract-preview="showContractPreview" />
                </v-col>
            </v-row>

            <!-- Tabuľka 2: Reálna osobná tabuľka investora -->
            <v-row v-if="isAuthenticated" class="mb-6">
                <v-col cols="12">
                    <personal-investments-table :investments="userInvestments" :annualRevenue="modeledAnnualRevenue"
                        :totalInvestments="totalInvestments" :payoutPercentage="modeledPayoutPercentage"
                        :current-user="userInfo" :is-staff="isStaff" :deleting-investment-ids="deletingInvestmentIds"
                        :loading="loadingUserInvestments" @start-investing="scrollToCalculator"
                        @refresh="loadUserInvestments" @show-contract="showContractDetail"
                        @download-contract="downloadContract" @invest-additional="handleAdditionalInvestment"
                        @delete-investment="handleDeleteInvestment" />
                </v-col>
            </v-row>

            <!-- Tabuľka 3: Koľko treba vrátiť investorom -->
            <v-row class="mb-6">
                <v-col cols="12">
                    <investment-overview-table :totalContracts="totalContracts" :uniqueInvestors="uniqueInvestors"
                        :totalInvestments="totalInvestments" :totalInvestmentLevels="investmentLevels"
                        :totalPayoutRequired="totalPayoutRequired" :actualPayouts="actualPayouts"
                        :maxInvestmentAmount="maxInvestmentAmount" />
                </v-col>
            </v-row>

            <!-- Tabuľka 4: Predpokladané ročné tržby -->
            <v-row class="mb-6">
                <v-col cols="12">
                    <revenue-forecast-table :realRevenues="realRevenues" :projects="projects"
                        :annualRevenue="modeledAnnualRevenue" @revenue-change="handleAnnualRevenueChange" />
                </v-col>
            </v-row>

            <!-- Informačná sekcia -->
            <v-row class="mb-6">
                <v-col cols="12">
                    <v-card elevation="2">
                        <v-card-title class="warning--text">
                            <v-icon left color="warning">mdi-information</v-icon>
                            Dôležité informácie
                        </v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-alert type="info" outlined dense>
                                        <div class="font-weight-bold mb-1">Minimálna výška investície</div>
                                        <div class="text-h6 primary--text">10 €</div>
                                        <div class="text-caption">Investovať môžete len násobky 10 €</div>
                                    </v-alert>
                                </v-col>
                                <!-- <v-col cols="12" md="4">
                                    <v-alert type="warning" outlined dense>
                                        <div class="font-weight-bold mb-1">Platnosť zmluvy</div>
                                        <div class="text-body-1 mb-3">
                                            Prečítajte si často kladené otázky a návody.
                                        </div>
                                        <v-btn color="info" outlined large>
                                            <v-icon left>mdi-book-open</v-icon>
                                            Dokumentácia
                                        </v-btn>
                                    </v-alert>
                                </v-col> -->
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <!-- Login redirect dialog -->
        <v-dialog v-model="loginDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title class="primary white--text">
                    <v-icon left color="white">mdi-account</v-icon>
                    Prihlásenie potrebné
                </v-card-title>
                <v-card-text class="pa-6">
                    <div class="text-center">
                        <v-icon size="80" color="primary" class="mb-4">mdi-account-circle</v-icon>
                        <div class="text-h6 mb-3">Pre investovanie sa musíte prihlásiť</div>
                        <div class="text-body-1 grey--text mb-4">
                            Vytvorte si účet alebo sa prihláste do existujúceho účtu.
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-btn color="grey" text @click="loginDialog = false">
                        Zrušiť
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="success" @click="redirectToRegister">
                        Registrácia
                    </v-btn>
                    <v-btn color="primary" @click="redirectToLogin">
                        Prihlásenie
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Contract Preview Dialog -->
        <contract-preview-dialog v-model="contractDialog" :investmentAmount="plannedInvestmentAmount"
            :userInfo="userInfo" :investmentBreakdown="contractBreakdown" @confirm="confirmInvestment" />

        <!-- Payment Details Dialog -->
        <payment-details-dialog v-model="paymentDetailsDialog" :amount="plannedInvestmentAmount"
            :variable-symbol="userInfo && userInfo.id ? userInfo.id : null" />

        <!-- Success Dialog -->
        <v-dialog v-model="successDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="success white--text">
                    <v-icon left color="white">mdi-check-circle</v-icon>
                    Investícia vytvorená
                </v-card-title>
                <v-card-text class="pa-6">
                    <div class="text-center">
                        <v-icon size="80" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
                        <div class="text-h6 mb-2">Úspešne ste vytvorili investíciu!</div>
                        <div class="text-body-1 mb-4">
                            Investícia vo výške <strong>{{ formatCurrency(plannedInvestmentAmount) }}</strong>
                            bola úspešne vytvorená.
                        </div>

                        <v-alert type="warning" outlined class="mb-4">
                            <strong>Nezabudnite uhradiť investíciu!</strong><br>
                            Použite platobné údaje z náhľadu zmluvy. Investícia sa stane aktívnou (a zobrazí sa nižšie)
                            až po pripísaní sumy
                            na náš účet.
                        </v-alert>

                        <div class="mb-4">
                            <div class="text-caption mb-1">Vaše investičné ID (variabilný symbol):</div>
                            <v-chip color="primary" large>{{ userInfo.id }}</v-chip>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" large @click="onSuccessContinue">
                        Pokračovať
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="deleteDialog" max-width="500px">
            <v-card>
                <v-card-title class="text-h6">Potvrdenie vymazania</v-card-title>
                <v-card-text>
                    Naozaj chcete vymazať investíciu
                    <strong v-if="deleteInvestmentTarget && deleteInvestmentTarget.contract_number">
                        #{{ deleteInvestmentTarget.contract_number }}
                    </strong>
                    <strong v-else-if="deleteInvestmentTarget">
                        #{{ deleteInvestmentTarget.id }}
                    </strong>
                    ? Táto akcia sa nedá vrátiť späť.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeDeleteDialog">Zrušiť</v-btn>
                    <v-btn color="error" :loading="isDeleteDialogLoading" @click="confirmDeleteInvestment">
                        Vymazať
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="deleteSnackbar.show" :color="deleteSnackbar.color" :timeout="6000">
            {{ deleteSnackbar.message }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="deleteSnackbar.show = false">Zavrieť</v-btn>
            </template>
        </v-snackbar>

        <!-- Loading overlay -->
        <v-overlay :value="loading">
            <div class="text-center">
                <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
                <div class="text-h6 mt-4">Spracováva sa...</div>
            </div>
        </v-overlay>
    </div>
</template>

<script>
// Import komponentov
import InvestmentCalculator from '@/components/invest/InvestmentCalculator.vue';
import PersonalInvestmentsTable from '@/components/invest/PersonalInvestmentsTable.vue';
import InvestmentOverviewTable from '@/components/invest/InvestmentOverviewTable.vue';
import RevenueForecastTable from '@/components/invest/RevenueForecastTable.vue';
import ContractPreviewDialog from '@/components/invest/ContractPreviewDialog.vue';
import PaymentDetailsDialog from '@/components/invest/PaymentDetailsDialog.vue';
import { APIService } from "@/http/APIService.js";
import { mapGetters } from "vuex";

const apiService = new APIService();


// Mock API service
// const APIService = {
//     getInvestmentStats() {
//         return Promise.resolve({
//             totalInvestments: 2687420, // done
//             actualPayouts: 370970.6, // done
//             totalContracts: 150, // done
//             uniqueInvestors: 89 // done
//         });
//     },

//     getRevenueForecast() {
//         return Promise.resolve({
//             defaultAnnualRevenue: 3420000
//         });
//     },

//     getUserInvestments() {
//         return Promise.resolve({
//             results: [
//                 {
//                     id: 1, // done
//                     contract_number: '123/1', // done
//                     investment_amount: 1000, // done
//                     coefficient: 5, // done
//                     payout_claim: 5000, // done
//                     status: 'active', // done
//                     created_at: '2024-01-15', // done
//                     payment_date: '2024-01-16', // done
//                 },
//                 {
//                     id: 2,
//                     contract_number: '123/2',
//                     investment_amount: 100000,
//                     coefficient: 5,
//                     payout_claim: 500000,
//                     status: 'active',
//                     created_at: '2024-02-01',
//                     payment_date: '2024-02-02',
//                 }
//             ]
//         });
//     },

//     getMe() {
//         return Promise.resolve({
//             id: 123,
//             first_name: 'Ján',
//             last_name: 'Novák',
//             email: 'jan.novak@example.com',
//             address: 'Bratislava, Slovensko'
//         });
//     },

//     createInvestment(data) {
//         return Promise.resolve({ success: true, id: Math.random() });
//     }
// };

export default {
    name: "InvestmentPlatform",

    components: {
        InvestmentCalculator,
        PersonalInvestmentsTable,
        InvestmentOverviewTable,
        RevenueForecastTable,
        ContractPreviewDialog,
        PaymentDetailsDialog
    },

    data() {
        return {
            loading: false,
            loadingUserInvestments: false,
            loginDialog: false,
            contractDialog: false,
            successDialog: false,
            plannedInvestmentAmount: 0,
            contractBreakdown: [],
            paymentDetailsDialog: false,

            realRevenues: [],

            // Default values based on Excel analysis
            defaultAnnualRevenue: 3420000, // Z Excel analýzy - prvý rok
            modeledAnnualRevenue: 3420000,
            modeledPayoutPercentage: 60,
            totalInvestments: 0, // Z Excel analýzy
            activeInvestmentsTotal: null,
            maxInvestmentAmount: 100,
            uniqueInvestors: 0,
            totalContracts: 0,

            // Investment levels with coefficients (z Excel komentárov)
            investmentLevels: [
                { threshold: 10, investment_amount: 10, coefficient: 5, total_payout: 50 },
                { threshold: 20, investment_amount: 10, coefficient: 4, total_payout: 40 },
                { threshold: 30, investment_amount: 10, coefficient: 3, total_payout: 30 },
                { threshold: 40, investment_amount: 10, coefficient: 2, total_payout: 20 },
                { threshold: 100, investment_amount: 60, coefficient: 1.5, total_payout: 90 }
            ],

            totalPayoutRequired: 230, // Z Excel analýzy
            actualPayouts: 0, // Z Excel tabuľky reálne tržby

            // Projects for revenue forecasting (z Excel)
            // projects: [
            //     { id: 1, name: "FICHEE TAXI", category: "transport", year1: 1200000, year2: 3600000, year3: 6000000 },
            //     { id: 2, name: "FICHEE RENTAL + SHARING", category: "transport", year1: 1200000, year2: 3600000, year3: 6000000 },
            //     { id: 3, name: "FICHEE KURIÉR", category: "transport", year1: 1200000, year2: 3600000, year3: 6000000 },
            //     { id: 4, name: "FICHEE GUARD", category: "security", year1: 1200000, year2: 3600000, year3: 6000000 },
            //     { id: 5, name: "Predaj z dvora", category: "retail", year1: 1200000, year2: 3600000, year3: 6000000 },
            //     { id: 6, name: "Holidaydom", category: "advertising", year1: 1200000, year2: 3600000, year3: 6000000 }
            // ],

            projects: [],
            deletingInvestmentIds: [],
            deleteDialog: false,
            isDeleteDialogLoading: false,
            deleteInvestmentTarget: null,
            deleteSnackbar: {
                show: false,
                message: '',
                color: 'success'
            },

            userInvestments: [],
            userInfo: {
                id: 123,
                first_name: 'Demo',
                last_name: 'Investor',
                email: 'demo@example.com',
                address: 'Demo adresa'
            }
        };
    },

    computed: {
        ...mapGetters("auth", [
            "isAuthenticated",
            "username",
            "isVerified",
            "isVerifiedSmsEmail",
            "isVerifiedBank",
            "isStaff",
            "getMemberStatus",
            "firstName",
            "lastName",
            "gender"
        ]),

        myInactiveInvestmentsTotal() {
            return this.userInvestments.reduce((sum, investment) => {
                const isActive = investment.status === true || investment.status === 1 || investment.status === 'true';
                if (isActive) {
                    return sum;
                }

                const amount = Number(investment.investment_amount);
                return sum + (Number.isFinite(amount) ? amount : 0);
            }, 0);
        }

    },

    watch: {
        isAuthenticated: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.loadUserInvestments();
                    this.loadUserInfo();
                }
            }
        }
    },

    mounted() {
        this.loadInitialData();
    },

    methods: {
        loadInitialData() {
            this.loading = true;

            Promise.all([
                apiService.getInvestmentStats(),
                apiService.getRevenueForecast(),
                apiService.getInvestmentProjects(),
                apiService.getInvestmentRevenues()
            ]).then(([statsResponse, revenueResponse, projectsResponse, revenueListResponse]) => {
                if (statsResponse) {
                    this.totalInvestments = statsResponse.totalInvestments;
                    this.activeInvestmentsTotal =
                        Number.isFinite(Number(statsResponse.totalActiveInvestments))
                            ? Number(statsResponse.totalActiveInvestments)
                            : Number.isFinite(Number(statsResponse.activeInvestments))
                                ? Number(statsResponse.activeInvestments)
                                : Number.isFinite(Number(statsResponse.active_total_investments))
                                    ? Number(statsResponse.active_total_investments)
                                    : null;
                    this.actualPayouts = statsResponse.actualPayouts || 0;
                    this.uniqueInvestors = statsResponse.uniqueInvestors;
                    this.totalContracts = statsResponse.totalContracts;
                }

                if (revenueResponse) {
                    // this.defaultAnnualRevenue = revenueResponse.defaultAnnualRevenue || this.defaultAnnualRevenue;
                    this.modeledAnnualRevenue = this.defaultAnnualRevenue;
                }

                if (projectsResponse) {
                    this.projects = projectsResponse.results;
                }

                if (revenueListResponse) {
                    this.realRevenues = revenueListResponse.results;
                }

                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },

        loadUserInvestments() {
            this.loadingUserInvestments = true;

            apiService.getUserInvestments().then((response) => {
                this.userInvestments = (response && response.results) ? response.results
                    : Array.isArray(response) ? response : [];
                this.loadingUserInvestments = false;
            }).catch((err) => {
                console.error('loadUserInvestments error:', err);
                this.loadingUserInvestments = false;
            });
        },

        loadUserInfo() {
            apiService.getMe().then((response) => {
                this.userInfo = response;
            });
        },

        handleInvestmentSubmission(amount) {
            if (!this.isAuthenticated) {
                this.loginDialog = true;
                return;
            }

            this.plannedInvestmentAmount = amount;
            this.loading = true;

            // Create the investment via API
            const investmentData = {
                investment_amount: amount
            };

            apiService.createInvestment(investmentData).then(() => {
                this.successDialog = true;
                this.loadUserInvestments();
                this.loadInitialData();
            }).catch((error) => {
                console.error('Chyba pri vytváraní investície:', error);
                // TODO: Show error toast/notification
            }).finally(() => {
                this.loading = false;
            });
        },

        handleAdditionalInvestment(amount) {
            this.handleInvestmentSubmission(amount);
        },

        handleDeleteInvestment(investment) {
            if (!investment || !investment.id) {
                return;
            }

            this.deleteInvestmentTarget = investment;
            this.deleteDialog = true;
        },

        closeDeleteDialog() {
            this.deleteDialog = false;
            this.$nextTick(() => {
                this.deleteInvestmentTarget = null;
            });
        },

        showDeleteSnackbar(message, color = 'success') {
            this.deleteSnackbar.message = message;
            this.deleteSnackbar.color = color;
            this.deleteSnackbar.show = true;
        },

        confirmDeleteInvestment() {
            const investment = this.deleteInvestmentTarget;
            if (!investment || !investment.id) {
                this.closeDeleteDialog();
                return;
            }

            const investmentLabel = investment.contract_number
                ? `#${investment.contract_number}`
                : `#${investment.id}`;

            this.isDeleteDialogLoading = true;

            if (!this.deletingInvestmentIds.includes(investment.id)) {
                this.deletingInvestmentIds.push(investment.id);
            }

            apiService.deleteInvestment(investment.id).then(() => {
                this.userInvestments = this.userInvestments.filter(item => item.id !== investment.id);
                this.loadUserInvestments();
                this.loadInitialData();
                this.showDeleteSnackbar(`Investícia ${investmentLabel} bola úspešne vymazaná.`, 'success');
                this.closeDeleteDialog();
            }).catch((error) => {
                const statusCode = error && error.response ? error.response.status : null;

                if (statusCode === 403) {
                    this.showDeleteSnackbar('Aktívnu investíciu nie je možné vymazať. Kontaktujte administrátora.', 'error');
                    return;
                }

                if (statusCode === 404) {
                    this.loadUserInvestments();
                    this.showDeleteSnackbar('Investícia nebola nájdená. Zoznam bol obnovený.', 'warning');
                    this.closeDeleteDialog();
                    return;
                }

                if (statusCode === 401) {
                    this.showDeleteSnackbar('Relácia vypršala. Prihláste sa znova.', 'error');
                    return;
                }

                this.showDeleteSnackbar('Investíciu sa nepodarilo vymazať. Skúste to znova.', 'error');
            }).finally(() => {
                this.deletingInvestmentIds = this.deletingInvestmentIds.filter(id => id !== investment.id);
                this.isDeleteDialogLoading = false;
            });
        },

        showContractPreview(data) {
            if (!this.isAuthenticated) {
                this.loginDialog = true;
                return;
            }

            this.plannedInvestmentAmount = data.amount;
            this.contractBreakdown = data.breakdown || [];
            this.contractDialog = true;
        },

        onSuccessContinue() {
            this.successDialog = false;
            this.paymentDetailsDialog = true;
        },

        showInvestmentSuccess() {
            // TODO Pre demo - priamo zobrazíme success dialog
            this.successDialog = true;
        },

        confirmInvestment(investmentData) {
            console.log(investmentData);
            this.loading = true;

            apiService.createInvestment(investmentData).then(() => {
                this.contractDialog = false;
                this.successDialog = true;
                this.loadUserInvestments();
                this.loadInitialData();
            }).catch((error) => {
                console.error('Chyba pri vytváraní investície:', error);
                // TODO V reálnej aplikácii by tu bol toast alebo iná notifikácia
            }).finally(() => {
                this.loading = false;
            });
        },

        handleAnnualRevenueChange(newRevenue) {
            this.modeledAnnualRevenue = newRevenue;
        },

        handlePayoutPercentageChange(newPercentage) {
            const parsed = Number(newPercentage);
            if (!Number.isFinite(parsed)) {
                return;
            }

            this.modeledPayoutPercentage = parsed;
        },

        scrollToCalculator() {
            if (this.$refs.calculator) {
                this.$refs.calculator.$el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        },

        showContractDetail(investment) {
            console.log('Show contract detail:', investment);
        },

        downloadContract(investment) {
            console.log('Download contract:', investment);
        },

        redirectToLogin() {
            this.loginDialog = false;
            this.$router.push('/login');
        },

        redirectToRegister() {
            this.loginDialog = false;
            this.$router.push('/register');
        },

        formatCurrency(amount) {
            // Handle null, undefined, empty string
            if (amount == null || amount === '') return '0€';

            // Convert string to number (handles Django DecimalField)
            const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

            // Check if conversion resulted in a valid number
            if (isNaN(numAmount)) return '0€';

            return new Intl.NumberFormat('sk-SK', {
                style: 'currency',
                currency: 'EUR'
            }).format(numAmount);
        }
    }
};
</script>

<style scoped>
.investment-platform {
    background-color: #f8f9fa;
    min-height: 100vh;
}

.platform-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.platform-header .v-card__title {
    color: white !important;
}

.platform-header .primary--text {
    color: white !important;
}

.v-container {
    max-width: 1400px;
}

@media (max-width: 600px) {
    .text-h3 {
        font-size: 1.8rem !important;
    }

    .text-h6 {
        font-size: 1.1rem !important;
    }

    .v-container {
        padding: 8px;
    }
}
</style>